"use client";

import type { Selection, SortDescriptor } from "@heroui/react";
import type { RowData } from "@tanstack/react-table";
declare module "@tanstack/react-table" {
  // biome-ignore lint/correctness/noUnusedVariables: TData and TValue are required to match TanStack's interface but unused in our extension.
  interface ColumnMeta<TData extends RowData, TValue> {
    headerClassName?: string;
    cellClassName?: string;
    /** Pin column to left or right edge during horizontal scroll */
    sticky?: "left" | "right";
  }
}
import { Table as HeroTable, type TableProps as HeroTableProps, TableBody, TableCell, TableColumn, TableHeader, TableRow } from "@heroui/table";
import { type ColumnDef, type ColumnFiltersState, flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, type OnChangeFn, type PaginationState, type Row, type RowSelectionState, type SortingState, type TableState, useReactTable } from "@tanstack/react-table";
import type { ReactNode } from "react";
import { useMemo, useRef } from "react";
import { useScrollShadow } from "../../hooks/useScrollShadow/useScrollShadow";
import { cn } from "../../lib/utils";
import type { PaginationLabels } from "../Pagination/Pagination";
import { Spinner } from "../Spinner";
import { useUiLabels } from "../UiLabels/useUiLabels";
import { mergeTableClassNames } from "./Table.utils";
import { TablePaginationFooter } from "./TablePaginationFooter";
export interface TableProps<TData> {
  /** Accessible label describing the table's purpose. Required for WCAG 1.3.1. */
  "aria-label": string;
  /** The data to display in the table. */
  data: TData[];
  /** The columns to display in the table. */
  columns: ColumnDef<TData>[];
  /** The state of the table, including sorting, pagination, and column filters. */
  state?: Partial<TableState>;
  /** The number of pages in the table. */
  pageCount?: number;
  /** The function to call when the sorting state changes. */
  onSortingChange?: OnChangeFn<SortingState>;
  /** The function to call when the pagination state changes. */
  onPaginationChange?: OnChangeFn<PaginationState>;
  /** The function to call when the column filters state changes. */
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  /** The function to call when the row selection state changes. */
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  /** Whether or not data will be sorted server-side. */
  manualSorting?: boolean;
  /** Whether or not data will be paginated server-side. */
  manualPagination?: boolean;
  /** Whether or not data will be filtered server-side. */
  manualFiltering?: boolean;
  /** The number of rows in the table. */
  rowCount?: number;
  /** Whether the table is loading. */
  isLoading?: boolean;
  /** Optional filters to display above the table. */
  filters?: ReactNode;
  /** Placement of the filters. */
  filtersPlacement?: "inside" | "outside";
  /** Enable row selection. Can be boolean or function for conditional selection per row. */
  enableRowSelection?: boolean | ((row: Row<TData>) => boolean);
  /** Selection mode: "single" allows one row, "multiple" allows many. Only used when enableRowSelection is true. */
  selectionMode?: "single" | "multiple";
  /** Function to get a unique ID for each row. Defaults to row index if not provided. */
  getRowId?: (originalRow: TData, index: number, parent?: Row<TData>) => string;
  /** Optional content to display when the table is empty. */
  emptyContent?: ReactNode;
  /** Custom class names for table elements. Will be merged with default classes. */
  classNames?: HeroTableProps["classNames"];
  /** Whether the header is sticky. */
  isHeaderSticky?: boolean;
  /** Override labels for pagination i18n support. */
  paginationLabels?: PaginationLabels;
  /** Optional per-row class hook (e.g. dim completed rows). Receives the row and returns a className string. */
  getRowClassName?: (row: Row<TData>) => string | undefined;
  /** Optional row-open handler. When set, clicking a row body calls this with the row; clicks on
   *  inner interactive elements (links, buttons, inputs, or `[data-no-row-click]`) are ignored. */
  onRowClick?: (row: Row<TData>) => void;
}
export function Table<TData>({
  "aria-label": ariaLabel,
  data,
  columns,
  isLoading,
  state = {
    pagination: {
      pageIndex: 0,
      pageSize: 50
    }
  },
  pageCount,
  onSortingChange,
  onPaginationChange,
  onColumnFiltersChange,
  onRowSelectionChange,
  manualSorting = true,
  manualPagination = true,
  manualFiltering = true,
  rowCount,
  filters,
  filtersPlacement = "inside",
  enableRowSelection,
  selectionMode = "multiple",
  getRowId,
  emptyContent = "No rows to display.",
  classNames,
  isHeaderSticky = true,
  paginationLabels,
  getRowClassName,
  onRowClick
}: TableProps<TData>) {
  const uiLabels = useUiLabels();
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualSorting,
    manualPagination,
    manualFiltering,
    pageCount,
    rowCount,
    enableRowSelection,
    getRowId,
    state: {
      sorting: state?.sorting,
      pagination: state?.pagination,
      columnFilters: state?.columnFilters,
      rowSelection: state?.rowSelection,
      columnVisibility: state?.columnVisibility
    },
    onSortingChange,
    onPaginationChange,
    onColumnFiltersChange,
    onRowSelectionChange
  });

  // Shift+click range selection tracking
  const lastClickedRowIdRef = useRef<string | null>(null);
  const didHandleShiftRef = useRef(false);
  const computeRangeSelection = (anchorId: string, targetId: string) => {
    const rows = table.getRowModel().rows;
    const anchorIdx = rows.findIndex(r => r.id === anchorId);
    const targetIdx = rows.findIndex(r_0 => r_0.id === targetId);
    if (anchorIdx === -1 || targetIdx === -1) return null;
    const start = Math.min(anchorIdx, targetIdx);
    const end = Math.max(anchorIdx, targetIdx);
    const currentSelection = table.getState().rowSelection ?? {};
    const isDeselecting = !!currentSelection[targetId];
    const visibleRowIds = new Set(rows.map(r_1 => r_1.id));
    const rangeSelection: RowSelectionState = {};

    // Preserve off-page selections
    for (const [key, value] of Object.entries(currentSelection)) {
      if (!visibleRowIds.has(key) && value) {
        rangeSelection[key] = true;
      }
    }
    if (isDeselecting) {
      const rangeRowIds = new Set<string>();
      for (let i = start; i <= end; i++) {
        if (rows[i].getCanSelect()) {
          rangeRowIds.add(rows[i].id);
        }
      }
      for (const [key_0, value_0] of Object.entries(currentSelection)) {
        if (visibleRowIds.has(key_0) && value_0 && !rangeRowIds.has(key_0)) {
          rangeSelection[key_0] = true;
        }
      }
    } else {
      for (const [key_1, value_1] of Object.entries(currentSelection)) {
        if (visibleRowIds.has(key_1) && value_1) {
          rangeSelection[key_1] = true;
        }
      }
      for (let i_0 = start; i_0 <= end; i_0++) {
        if (rows[i_0].getCanSelect()) {
          rangeSelection[rows[i_0].id] = true;
        }
      }
    }
    return rangeSelection;
  };
  const handlePointerDownCapture = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!enableRowSelection || !onRowSelectionChange) return;
    const target = e.target as HTMLElement;
    const rowEl = target.closest("[data-row-id]");
    if (!rowEl) return;
    const rowId = rowEl.getAttribute("data-row-id");
    if (!rowId) return;
    if (e.shiftKey && lastClickedRowIdRef.current !== null) {
      // Compute and apply range selection immediately, then stop the event
      // so React Aria never processes it (avoids duplicate onSelectionChange issues)
      const result = computeRangeSelection(lastClickedRowIdRef.current, rowId);
      if (result) {
        e.stopPropagation();
        e.preventDefault();
        didHandleShiftRef.current = true;
        onRowSelectionChange(result);
        return;
      }
    }
    lastClickedRowIdRef.current = rowId;
  };

  // Block click/pointerup events after a shift+pointerdown so React Aria
  // and checkbox native handlers don't fire a second selection change.
  const handleClickCapture = (e_0: React.MouseEvent<HTMLDivElement>) => {
    if (didHandleShiftRef.current) {
      didHandleShiftRef.current = false;
      e_0.stopPropagation();
      e_0.preventDefault();
    }
  };
  const handleRowClick = (e_1: React.MouseEvent<HTMLDivElement>) => {
    if (!onRowClick) return;
    const target_0 = e_1.target as HTMLElement;
    // Inner interactive elements keep their own behavior — no per-cell stopPropagation needed.
    if (target_0.closest("a, button, [role='button'], input, label, [data-no-row-click]")) return;
    const rowId_0 = target_0.closest("[data-row-id]")?.getAttribute("data-row-id");
    if (!rowId_0) return;
    const row = table.getRowModel().rows.find(r_2 => r_2.id === rowId_0);
    if (row) onRowClick(row);
  };
  const sorting = state?.sorting;

  // Convert TanStack sorting to HeroUI SortDescriptor
  const sortDescriptor: SortDescriptor = sorting?.length ? {
    column: sorting[0].id,
    direction: sorting[0].desc ? "descending" : "ascending"
  } : {
    column: "",
    direction: "ascending"
  };
  const handleSortChange = (descriptor: SortDescriptor) => {
    if (onSortingChange && descriptor.column) {
      const newSort = [{
        id: descriptor.column,
        desc: descriptor.direction === "descending"
      }];
      onSortingChange(newSort as SortingState);

      // Reset to page 1 when sorting changes
      if (onPaginationChange) {
        table.setPageIndex(0);
      }
    }
  };

  // Convert TanStack rowSelection to HeroUI selectedKeys
  const selectedKeys = useMemo(() => {
    const rowSelection = state?.rowSelection ?? {};
    return new Set(Object.keys(rowSelection).filter(key_2 => rowSelection[key_2]));
  }, [state?.rowSelection]);

  // Handle HeroUI selection change → TanStack format
  const handleSelectionChange = (keys: Selection) => {
    if (!onRowSelectionChange) return;
    if (keys === "all") {
      // Select all visible rows while preserving selections from other pages
      const allSelected: RowSelectionState = {
        ...(state?.rowSelection ?? {})
      };
      for (const row_0 of table.getRowModel().rows) {
        allSelected[row_0.id] = true;
      }
      onRowSelectionChange(allSelected);
    } else {
      const visibleRowIds_0 = new Set(table.getRowModel().rows.map(row_1 => row_1.id));
      const newSelection: RowSelectionState = {};

      // Keep off-page selections
      for (const [key_3, value_2] of Object.entries(state?.rowSelection ?? {})) {
        if (!visibleRowIds_0.has(key_3) && value_2) {
          newSelection[key_3] = true;
        }
      }

      // Add current page selections
      for (const key_4 of keys) {
        newSelection[String(key_4)] = true;
      }
      onRowSelectionChange(newSelection);
    }
  };

  // Hides flash of pagination while pageCount is still calculating
  // getPageCount() needs state.pagination; a negative count means unknown, so it still shows.
  const resolvedPageCount = state?.pagination ? table.getPageCount() : 0;
  const shouldShowPagination = !isLoading && !!state?.pagination && (resolvedPageCount < 0 || resolvedPageCount > 1);
  // Auto-detect the scrollable element within HeroUI's table structure
  // See useScrollShadow/README.md for more details.
  const {
    containerRef,
    canScrollLeft,
    canScrollRight
  } = useScrollShadow<HTMLDivElement>();
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: delegates to rows, which remain keyboard-navigable via the grid's own roles.
    // biome-ignore lint/a11y/noStaticElementInteractions: click delegation wrapper around the accessible HeroUI grid.
    <div ref={containerRef} className="relative h-full" onPointerDownCapture={handlePointerDownCapture} onClickCapture={handleClickCapture} onClick={handleRowClick}>
      {/* Left scroll shadow - positioned to right of sticky column when present */}
      <div aria-hidden="true" className={cn("pointer-events-none absolute top-0 bottom-0 left-0 z-10 w-6 bg-linear-to-r from-background to-transparent transition-opacity duration-200 dark:from-zinc-900", canScrollLeft ? "opacity-100" : "opacity-0")} />
      {/* Right scroll shadow - positioned to left of sticky column when present */}
      <div aria-hidden="true" className={cn("pointer-events-none absolute top-0 right-0 bottom-0 z-10 w-6 bg-linear-to-l from-background to-transparent transition-opacity duration-200 dark:from-zinc-900", canScrollRight ? "opacity-100" : "opacity-0")} />
      <HeroTable isHeaderSticky={isHeaderSticky} aria-label={ariaLabel} sortDescriptor={sortDescriptor} onSortChange={handleSortChange} selectionMode={enableRowSelection ? selectionMode : "none"} selectedKeys={enableRowSelection ? selectedKeys : undefined} onSelectionChange={enableRowSelection ? handleSelectionChange : undefined} checkboxesProps={{
        classNames: {
          wrapper: "before:border-default-300 after:bg-primary"
        }
      }} topContent={filters} topContentPlacement={filtersPlacement} bottomContentPlacement="outside" bottomContent={shouldShowPagination ? <div className="pl-4">
              <TablePaginationFooter totalPages={table.getPageCount()} pageIndex={table.getState().pagination?.pageIndex} totalItems={rowCount} perPage={table.getState().pagination?.pageSize} onChange={page => table.setPageIndex(page - 1)} paginationLabels={paginationLabels} />
            </div> : null} classNames={mergeTableClassNames({
        // The scroll wrapper flexes so the table body scrolls while the `outside`
        // pagination footer stays pinned at the bottom instead of overflowing into
        // blank space. With no footer, flex-1 fills the base just like h-full did.
        base: "h-full min-h-0",
        wrapper: "min-h-0 flex-1 rounded-t-medium rounded-b-none border-0! border-b! border-divider bg-bg-container-surface-usual p-0! shadow-none!",
        thead: "[&>tr]:first:shadow-none!",
        th: cn("bg-bg-container-surface-dimmed font-medium font-mono text-text-body-neutral-onMatch-faint text-xs uppercase tracking-wider", enableRowSelection && "first:w-10 first:px-2"),
        td: enableRowSelection ? "first:w-10 first:px-2" : undefined
      }, classNames)} color="primary">
        <TableHeader>
          {table.getHeaderGroups()[0]?.headers.map(header => <TableColumn key={header.column.id} allowsSorting={header.column.getCanSort()} aria-sort={sorting?.length && sorting[0].id === header.column.id ? sorting[0].desc ? "descending" : "ascending" : header.column.getCanSort() ? "none" : undefined} width={header.column.getSize()} minWidth={header.column.columnDef.minSize} maxWidth={header.column.columnDef.maxSize} className={cn(header.column.columnDef.meta?.headerClassName, header.column.columnDef.meta?.sticky === "right" && "sm:-right-4 sticky right-0 z-20 bg-bg-container-surface-dimmed pr-0 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.1)] sm:pr-4", header.column.columnDef.meta?.sticky === "left" && "-left-4 sticky z-20 bg-bg-container-surface-dimmed pl-4 shadow-[8px_0_8px_-8px_rgba(0,0,0,0.1)]")}>
              {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
            </TableColumn>)}
        </TableHeader>
        <TableBody isLoading={isLoading} loadingContent={<Spinner aria-label={uiLabels.table.loading} />} emptyContent={emptyContent}>
          {table.getRowModel().rows.map(row_2 => <TableRow key={row_2.id} data-row-id={row_2.id} className={cn(getRowClassName?.(row_2), onRowClick && "cursor-pointer")}>
              {row_2.getVisibleCells().map(cell => <TableCell key={cell.id} className={cn(cell.column.columnDef.meta?.cellClassName, cell.column.columnDef.meta?.sticky === "right" && "sm:-right-4 sticky right-0 z-10 bg-bg-container-surface-usual pr-0 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.1)] sm:pr-4", cell.column.columnDef.meta?.sticky === "left" && "-left-4 sticky z-10 bg-bg-container-surface-usual pl-4 shadow-[8px_0_8px_-8px_rgba(0,0,0,0.1)]")}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>)}
            </TableRow>)}
        </TableBody>
      </HeroTable>
    </div>
  );
}